// İSG uzmanı portalı — izin detayında personel/araç belge listesinin PAYLAŞILAN satır görünümü
// (owner review 2026-09-28, "chips look very crowded"). EP `PersonnelDocuments.tsx` ve izin
// detayının o zamanki araç belge listesi bu satırı BİREBİR AYNALIYORDU (yalnız tür etiketi + i18n
// anahtar önekleri farklıydı) — B5-3'te aynı düzeltmenin İKİ dosyaya ELLE taşınması gerekmişti; bu
// dosya o kopyayı KALDIRIR, tek bir satır bileşeni her iki Sheet'i besler.
//
// **Carbon "exceptions loud, normal quiet" (owner direction).** Eski satır her belge için İKİ
// dolu hap İSTİFLİYORDU (geçerlilik + onay) — "Uygun"+"Onaylandı" gibi sağlıklı satırlar problem
// satırları kadar YÜKSEK SESLE bağırıyordu. Artık: her iki boyut da "iyi" ise (`quiet=true`)
// HİÇ hap YOK — yalnız 16px yeşil onay ikonu + `quietLabel` sessiz metin. Bir boyut kötüyse
// yalnız O boyutun hapı (satır İÇİNDE, YATAY — asla dikey istif) görünür; "iyi" olan boyut(lar)ın
// anlamı GÖRSEL OLARAK düşer ama `srExtra` ile ekran okuyucuya KAYBOLMADAN geri eklenir (WCAG —
// renk/hap TEK taşıyıcı olmasın kuralının metin karşılığı).
//
// **Aksiyon sütunu sabit 3 slot'tur (`DocumentActionSlot`, her biri 40px = Button `sm`
// genişliği).** Eskiden Onayla/Reddet koşullu render ediliyordu (`Approved` iken Onayla YOK,
// `Rejected` iken Reddet YOK) — bu, `justify-end` grubunun TOPLAM genişliğini satır başına
// değiştiriyor, İndir ikonunun x konumunu satırdan satıra KAYDIRIYORDU (owner review ekran
// görüntüsü). Şimdi her slot HER ZAMAN render edilir (boşsa boş `<span>`), İndir/Onayla/Reddet
// her satırda AYNI x'te durur.
//
// **Başlık satırı sr-only (owner review — "the heavy gray 'Tür' table header adds nothing in a
// 480px Sheet").** `<th>` hâlâ gerçek bir tablo başlığıdır (ekran okuyucu ilişkisi KORUNUR),
// yalnız görsel dolgusu kaldırılır — çağıran dosyalar bunu kendi `TableHeader`'ında yapar (bu
// dosyanın işi değil, satır/gövde).
//
// Shared by web-expert and web-supplier since GL #25 (vehicle Sheets in both portals).

import { Check, Hourglass, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { TableCell, TableRow } from './Table';
import { Tag, type TagColor } from './Tag';

/** Tek bir "problem" hapı — yalnız kötü boyutlar için üretilir (bkz. dosya başlığı). */
export interface DocumentStatusTag {
  tone: TagColor;
  label: string;
  /** 'hourglass': "Süresi yaklaşıyor" "Onay bekliyor"dan görsel olarak ayrışsın (renk tek taşıyıcı olmasın). */
  icon?: 'hourglass';
}

/** 40px sabit aksiyon slotu — boşken bile genişliğini KORUR (bkz. dosya başlığı, İndir kayması). */
export function DocumentActionSlot({ children }: { children?: ReactNode }) {
  return <span className="flex w-10 shrink-0 items-center justify-center">{children}</span>;
}

export interface DocumentRowProps {
  title: ReactNode;
  fileName?: string | null;
  validityRangeText: string;
  /** Her iki durum boyutu da "iyi" mi — true ise hap YERİNE sessiz onay göstergesi render edilir. */
  quiet: boolean;
  /** `quiet` iken görünen tek metin (örn. "Onaylandı"). */
  quietLabel: string;
  /** `quiet` DEĞİLKEN yalnız problemli boyutlar — asla ikiden fazla, asla dikey istif. */
  tags: DocumentStatusTag[];
  /** Satır başlığının eski (artık görsel olmayan) "Durum"/"Onay" sütun etiketi — ekran okuyucu
   * bağlamı için hem sessiz hem hap yolunda TEK sr-only önek olarak korunur. */
  statusContextLabel: string;
  /** Görsel olarak düşen "iyi" boyutun metni (bkz. dosya başlığı) — yoksa hiçbir şey kaybolmamış demektir. */
  srExtra?: string;
  rejectionReasonText?: string | null;
  actions: ReactNode;
  /** Satır-içi reddet gerekçesi editörü (`ControlCheckRow` deseni) — açıkken ikinci bir `TableRow`. */
  editorRow?: ReactNode;
  /** Belgenin serbest notu (kullanıcı metni — YALNIZ metin olarak render edilir, asla HTML). */
  noteText?: string | null;
  /** Kilit gerekçesi (ör. "Kullanımda: silinemez") — ikon + metin; renk tek taşıyıcı değildir. */
  lockedText?: string | null;
  /** Kaynak izi (ör. "AI v3") — `fileName`'den sonra aynı satırda; `fileName` yoksa tek başına. */
  provenance?: ReactNode;
}

export function DocumentRow({
  title,
  fileName,
  validityRangeText,
  quiet,
  quietLabel,
  tags,
  statusContextLabel,
  srExtra,
  rejectionReasonText,
  actions,
  editorRow,
  noteText,
  lockedText,
  provenance,
}: DocumentRowProps) {
  return (
    <>
      {/* `carbon:h-auto carbon:min-h-[var(--row-h)]` + `carbon:py-2` on both cells (coordinator
          polish, same precedent as `PermitsListPage.tsx`'s 2-line title cell, S33-8 review fix
          #6) — Carbon's `Table`/`TableCell` zero out row padding and pin a 40px height for its
          normal single-line rows (`carbon:py-0` + `carbon:h-[var(--row-h)]`); this row is 2-3
          lines, so without an override the name touched the divider above and the status line
          touched the one below. */}
      <TableRow className="carbon:h-auto carbon:min-h-[var(--row-h)]">
        <TableCell truncate={false} className="min-w-0 font-medium text-fg carbon:py-2">
          <div className="flex min-w-0 flex-col gap-1 [overflow-wrap:anywhere]">
            <span>{title}</span>
            {fileName || provenance ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-normal text-muted">
                {fileName ? <span>{fileName}</span> : null}
                {provenance}
              </div>
            ) : null}
            {noteText ? (
              <span className="line-clamp-2 text-xs font-normal text-muted" title={noteText}>
                {noteText}
              </span>
            ) : null}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-normal text-muted">
              {validityRangeText ? <span className="tnum">{validityRangeText}</span> : null}
              <span className="sr-only">{statusContextLabel}</span>
              {quiet ? (
                <span className="inline-flex items-center gap-1">
                  <Check className="h-4 w-4 text-success" aria-hidden="true" />
                  <span>{quietLabel}</span>
                </span>
              ) : (
                <span className="inline-flex flex-wrap items-center gap-1">
                  {tags.map((tag) => (
                    <Tag
                      key={`${tag.tone}-${tag.label}`}
                      color={tag.tone}
                      className="h-auto min-h-6 max-w-full whitespace-normal py-0.5"
                      icon={tag.icon === 'hourglass' ? <Hourglass aria-hidden="true" /> : undefined}
                    >
                      {tag.label}
                    </Tag>
                  ))}
                </span>
              )}
              {srExtra ? <span className="sr-only">{srExtra}</span> : null}
            </div>
            {rejectionReasonText ? (
              <span className="text-xs text-danger">{rejectionReasonText}</span>
            ) : null}
            {lockedText ? (
              <span className="inline-flex items-center gap-1 text-xs font-normal text-muted">
                <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {lockedText}
              </span>
            ) : null}
          </div>
        </TableCell>
        <TableCell truncate={false} className="carbon:py-2">
          <div className="flex h-full shrink-0 items-center justify-end gap-1">{actions}</div>
        </TableCell>
      </TableRow>
      {editorRow}
    </>
  );
}
